import {
  Band,
  GroupShell,
  LedgerHeader,
  Pagination,
  Text,
} from '@billda/design-system';
import { ACTIVITY_COLLAPSE, ACTIVITY_COLUMNS } from './columns';
import { ActivityLedgerRow } from './ActivityLedgerRow';
import type {
  ActivityLedgerRowModel,
  ActivityWeekGroup,
} from './toActivityRows';

interface ActivityLedgerProps {
  weeks: ActivityWeekGroup[];
  /** Only the weeks the user explicitly collapsed. */
  collapsedWeeks: ReadonlySet<string>;
  onToggleWeek: (key: string) => void;
  onOpenRow: (row: ActivityLedgerRowModel) => void;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export function ActivityLedger({
  weeks,
  collapsedWeeks,
  onToggleWeek,
  onOpenRow,
  totalCount,
  page,
  pageSize,
  onPageChange,
}: ActivityLedgerProps) {
  const first = totalCount === 0 ? 0 : page * pageSize + 1;
  const last = Math.min((page + 1) * pageSize, totalCount);
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <>
      <LedgerHeader columns={ACTIVITY_COLUMNS} collapse={ACTIVITY_COLLAPSE} />
      {weeks.map(week => (
        <GroupShell
          key={week.key}
          open={!collapsedWeeks.has(week.key)}
          onToggle={() => onToggleWeek(week.key)}
          header={(open, toggle) => (
            <Band
              collapse={ACTIVITY_COLLAPSE}
              primary={week.label}
              secondary={week.range}
              open={open}
              onToggle={toggle}
              data-testid="activity-group-header"
            />
          )}
        >
          {week.rows.map(row => (
            <ActivityLedgerRow
              key={row.id}
              row={row}
              columns={ACTIVITY_COLUMNS}
              collapse={ACTIVITY_COLLAPSE}
              onOpen={onOpenRow}
            />
          ))}
        </GroupShell>
      ))}
      <div className="flex flex-wrap items-center justify-center gap-4 py-4">
        <Text variant="caption" color="secondary">
          Showing {first}-{last} of {totalCount}
        </Text>
        <Pagination
          count={pageCount}
          page={page + 1}
          onChange={value => {
            onPageChange(value - 1);
          }}
        />
      </div>
    </>
  );
}
